import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from './App';
import { supabase } from '@/lib/supabase/client';
import { bootWebVitalsBeacon } from '@/lib/seo/beacon';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // quran_core data is immutable. Cache aggressively.
      staleTime: 1000 * 60 * 60, // 1 hour
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Query-key prefixes that hold per-user data. Listed in one place so
 * adding a new user-scoped query is a single-line change:
 *   1. Register the new hook (e.g. `['notes', userId]`) somewhere in src/hooks.
 *   2. Add the prefix tuple to this array.
 *   3. The auth handler below will purge it on SIGNED_OUT and refresh
 *      it on SIGNED_IN / USER_UPDATED.
 *
 * TanStack Query matches by prefix, so `['qari-profile']` removes
 * every `['qari-profile', userId]` entry regardless of suffix.
 */
const USER_SCOPED_QUERY_KEYS = [
  ['user_settings'],
  ['bookmarks'],
  ['bookmarks-count'],
  ['reading_progress'],
  ['reading-progress'],
  ['reading-streak'],
  ['ayah-reads-total'],
  ['ayah-reads-by-surah'],
  ['qari-profile'],
  ['qari-documents'],
] as const;

// Push every Supabase auth event into the TanStack Query cache so any
// hook that reads ['session'] re-renders. This is the single point of
// reactive truth for auth state in the app.
//
// On SIGNED_OUT we *remove* per-user queries instead of just invalidating
// them: invalidation would mark them stale, but their `enabled` flags are
// gated on `session.data`, so they would never refetch — and the previous
// user's data would linger in the cache for any code path that read it
// directly. removeQueries actually purges.
supabase.auth.onAuthStateChange((event, session) => {
  queryClient.setQueryData(['session'], session);

  if (event === 'SIGNED_OUT') {
    // Purge per-user cache entries so the previous user's data cannot
    // leak into the next signed-in session on the same tab.
    for (const key of USER_SCOPED_QUERY_KEYS) {
      queryClient.removeQueries({ queryKey: key });
    }
    return;
  }

  if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
    // Refresh per-user caches under the (possibly new) identity.
    for (const key of USER_SCOPED_QUERY_KEYS) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  }
  // INITIAL_SESSION and TOKEN_REFRESHED don't need to touch caches.
});

// Boot the web-vitals beacon (data source #6 for /admin/seo). Best-effort,
// no-op if the endpoint is unreachable.
bootWebVitalsBeacon();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider
        router={router}
        future={{ v7_startTransition: true }}
      />
    </QueryClientProvider>
  </StrictMode>,
);
